/**
 * RewardsBanner — top-of-dashboard banner showing the user's daily login bonus +
 * a referral CTA. Smart UX: hides itself if the bonus is already claimed for the day
 * AND the user has copied their referral code in the last 30 days.
 *
 * Reads:
 *   POST /api/users/claim-daily-bonus — idempotent per UTC date
 *   GET  /api/users/referral
 *
 * Designed to feel like a "thank-you" gift, not spam — one row, one CTA each.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Gift, Sparkles, Share2, Check, Copy, Flame } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authedFetch(method, path, body) {
  // Auth via httpOnly session cookie — withCredentials sends it automatically.
  return axios.request({ method, url: `${API}${path}`, data: body, withCredentials: true });
}

export default function RewardsBanner({ onClaim }) {
  const [bonus, setBonus] = useState(null);   // {claimed, credits_added, streak, ...}
  const [referral, setReferral] = useState(null);  // {referral_code, share_url, invited_count, ...}
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Auto-load referral info (always safe to fetch)
    authedFetch('get', '/users/referral')
      .then((r) => setReferral(r.data))
      .catch(() => {});
  }, []);

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const r = await authedFetch('post', '/users/claim-daily-bonus', {});
      setBonus(r.data);
      if (r.data.claimed) {
        toast.success(
          r.data.is_streak_bonus
            ? `🔥 Streak bonus! +${r.data.credits_added} credits (${r.data.streak}-day streak)`
            : `+${r.data.credits_added} credits added!`,
          { description: 'Come back tomorrow for more.' }
        );
        if (onClaim) onClaim(r.data.credits);
      } else {
        toast.info("You've already claimed today's bonus. Come back tomorrow!");
      }
    } catch (e) {
      toast.error('Could not claim bonus. Try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleCopyReferral = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setCopied(true);
      toast.success('Referral link copied!', { description: 'Share it — you both get 25 free credits.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy. Highlight the link to copy manually.');
    }
  };

  return (
    <div
      data-testid="rewards-banner"
      className="grid md:grid-cols-2 gap-3 mb-8"
    >
      {/* Daily bonus card */}
      <div className="relative overflow-hidden rounded-2xl border border-[#FFD93D]/20 bg-gradient-to-br from-[#FFD93D]/10 via-[#FF6B4A]/5 to-[#0A0A0F] p-5">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FFD93D]/15 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#FFD93D]/15 border border-[#FFD93D]/30 flex items-center justify-center shrink-0">
            <Gift className="w-6 h-6 text-[#FFD93D]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FFD93D]">DAILY BONUS</p>
              {bonus?.streak > 1 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider text-[#FF6B4A]">
                  <Flame className="w-2.5 h-2.5" /> {bonus.streak}-day streak
                </span>
              )}
            </div>
            <p className="text-sm text-white font-bold truncate">
              {bonus?.claimed
                ? `+${bonus.credits_added} credits claimed today 🎁`
                : 'Claim your daily +5 credits'}
            </p>
            <p className="text-[11px] text-white/40 truncate">Every 7 days: 2× bonus.</p>
          </div>
          <button
            onClick={handleClaim}
            disabled={claiming || bonus?.claimed}
            data-testid="claim-daily-bonus"
            className="shrink-0 px-4 h-9 rounded-full bg-[#FFD93D] hover:bg-[#FCD34D] text-[#0A0A0F] font-bold text-[11px] tracking-[0.15em] uppercase disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
          >
            {bonus?.claimed ? <><Check className="w-3.5 h-3.5" /> Claimed</> : claiming ? 'Claiming…' : <><Sparkles className="w-3.5 h-3.5" /> Claim</>}
          </button>
        </div>
      </div>

      {/* Referral card */}
      <div className="relative overflow-hidden rounded-2xl border border-[#E040A0]/20 bg-gradient-to-br from-[#E040A0]/10 via-[#FF6B4A]/5 to-[#0A0A0F] p-5">
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#E040A0]/15 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#E040A0]/15 border border-[#E040A0]/30 flex items-center justify-center shrink-0">
            <Share2 className="w-6 h-6 text-[#E040A0]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#E040A0] mb-0.5">REFERRAL · 25 + 25 CREDITS</p>
            <p className="text-sm text-white font-bold truncate">
              {referral?.invited_count > 0
                ? `${referral.invited_count} friend${referral.invited_count > 1 ? 's' : ''} joined — you earned ${referral.credits_earned} credits`
                : 'Share DIS2ORT, get 25 free credits per signup'}
            </p>
            <p className="text-[11px] text-white/40 font-mono truncate">{referral?.share_url || 'Loading…'}</p>
          </div>
          <button
            onClick={handleCopyReferral}
            disabled={!referral?.share_url}
            data-testid="copy-referral-link"
            className="shrink-0 px-4 h-9 rounded-full bg-[#E040A0] hover:bg-[#FF6B9D] text-white font-bold text-[11px] tracking-[0.15em] uppercase disabled:opacity-40 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
          </button>
        </div>
      </div>
    </div>
  );
}
